// src/pages/reports/CourierPerformancePage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  TrendingUp,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Download,
  RefreshCw,
  Calendar,
  Store,
  Truck,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Loader2,
  Search,
  Eye,
  EyeOff,
  X,
  AlertCircle,
  Check
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useCourierPerformance, NOT_UPDATED_VALUE } from '../../hooks/useCourierPerformance';
import { getCourierPerformanceDetails } from '../../services/api/courierPerformance';
import { formatCurrency } from '../../utils/currencyUtil';
import { useAuth } from '../../hooks/useAuth';
import { getShops } from '../../services/api/shops';
import { getBusinessCouriers, getCourierStatuses } from '../../services/api/businessCourier';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import styles from './CourierPerformancePage.module.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Mirrors FilterPanel's MultiSelect: pending/committed pattern with Select All, Clear, Apply, Cancel
const CourierStatusMultiSelect = ({ options, committedIds, onApply }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingIds, setPendingIds] = useState([]);
  const wrapperRef = useRef(null);

  const handleOpen = () => {
    setPendingIds(committedIds);
    setSearchTerm('');
    setIsOpen(true);
  };

  const handleDiscard = () => {
    setPendingIds(committedIds);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleDone = () => {
    onApply(pendingIds);
    setSearchTerm('');
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        handleDiscard();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, committedIds]);

  const handleToggle = (id) => {
    setPendingIds(prev =>
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  const filteredOptions = searchTerm
    ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  const handleSelectAll = () => setPendingIds(filteredOptions.map(o => o.id));
  const handleClearAll = () => setPendingIds([]);

  const getButtonLabel = () => {
    if (committedIds.length === 0) return 'All Courier Statuses';
    if (committedIds.length === 1) {
      const opt = options.find(o => String(o.id) === String(committedIds[0]));
      return opt ? opt.label : '1 selected';
    }
    return `${committedIds.length} selected`;
  };

  const hasPendingChanges =
    JSON.stringify([...pendingIds].sort()) !== JSON.stringify([...committedIds].sort());
  const pendingCount = pendingIds.length;

  return (
    <div ref={wrapperRef} className={styles.msWrapper}>
      <button
        type="button"
        onClick={isOpen ? handleDiscard : handleOpen}
        className={`${styles.msButton} ${isOpen ? styles.msOpen : ''} ${committedIds.length > 0 ? styles.msHasValue : ''}`}
      >
        <span className={styles.msLabel}>{getButtonLabel()}</span>
        <ChevronDown className={`${styles.msArrow} ${isOpen ? styles.msRotated : ''}`} size={18} />
      </button>

      {isOpen && (
        <div className={styles.msDropdown}>
          {options.length > 5 && (
            <div className={styles.msSearch}>
              <Search size={14} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className={styles.msSearchInput}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          )}

          {filteredOptions.length > 0 && (
            <div className={styles.msActions}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleSelectAll} className={styles.msActionButton}>
                Select All
              </button>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleClearAll} className={styles.msActionButton}>
                Clear
              </button>
            </div>
          )}

          <div className={styles.msOptions}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => {
                const isSelected = pendingIds.includes(opt.id);
                return (
                  <label
                    key={opt.id}
                    className={`${styles.msOption} ${isSelected ? styles.msOptionSelected : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggle(opt.id)}
                      className={styles.msCheckbox}
                    />
                    <span className={styles.msCheckboxCustom}>
                      {isSelected && <Check size={14} />}
                    </span>
                    <span className={styles.msOptionLabel}>{opt.label}</span>
                  </label>
                );
              })
            ) : (
              <div className={styles.msEmpty}>No options found</div>
            )}
          </div>

          <div className={styles.msFooter}>
            {pendingCount > 0 && (
              <span className={styles.msPendingCount}>{pendingCount} selected</span>
            )}
            <div className={styles.msFooterActions}>
              <button type="button" onClick={handleDiscard} className={styles.msCancelButton}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDone}
                className={`${styles.msApplyButton} ${hasPendingChanges ? styles.msApplyButtonActive : ''}`}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ExportProgressModal = ({ isOpen, onClose, status, error, totalRecords, fetchedRecords }) => {
  if (!isOpen) return null;

  const pct = totalRecords > 0 ? Math.round((fetchedRecords / totalRecords) * 100) : 0;
  const isDone = status === 'done';
  const isFailed = status === 'error';
  const isRunning = status === 'running';

  return (
    <div className={styles.exportModalOverlay}>
      <div className={styles.exportModal}>
        <div className={styles.exportModalHeader}>
          <div className={styles.exportModalTitle}>
            <Download size={20} />
            <span>Exporting Orders</span>
          </div>
          {(isDone || isFailed) && (
            <button onClick={onClose} className={styles.exportModalClose}><X size={18} /></button>
          )}
        </div>
        <div className={styles.exportModalBody}>
          {isRunning && <div className={styles.exportSpinner}><Loader2 size={32} className={styles.spinnerIcon} /></div>}
          {isDone && <div className={styles.exportSuccessIcon}><CheckCircle size={32} /></div>}
          {isFailed && <div className={styles.exportErrorIcon}><AlertCircle size={32} /></div>}
          <p className={styles.exportStatusText}>
            {isFailed
              ? error || 'Export failed. Please try again.'
              : isDone
              ? 'Export complete! Your file has been downloaded.'
              : `Fetching orders… ${fetchedRecords.toLocaleString()} of ${totalRecords > 0 ? totalRecords.toLocaleString() : '?'}`}
          </p>
          {!isFailed && totalRecords > 0 && (
            <>
              <div className={styles.exportProgressBar}>
                <div
                  className={`${styles.exportProgressFill} ${isDone ? styles.exportProgressDone : ''}`}
                  style={{ width: `${isDone ? 100 : pct}%` }}
                />
              </div>
              <p className={styles.exportProgressPct}>{isDone ? 100 : pct}%</p>
            </>
          )}
          {!isFailed && totalRecords === 0 && isRunning && (
            <div className={styles.exportProgressBar}>
              <div className={styles.exportProgressIndeterminate} />
            </div>
          )}
        </div>
        {(isDone || isFailed) && (
          <div className={styles.exportModalFooter}>
            <button onClick={onClose} className={styles.secondaryButton}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

const CourierPerformancePage = () => {
  const { user } = useAuth();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const {
    summaryData,
    detailsData,
    isLoadingSummary,
    isLoadingDetails,
    fetchSummary,
    fetchDetails,
    resetAll,
    resetDetails
  } = useCourierPerformance();

  // Filter Options State
  const [filterOptions, setFilterOptions] = useState({
    shops: [],
    couriers: [],
    courierStatuses: []
  });
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);

  // Filter State
  const [filters, setFilters] = useState({
    groupBy: 'date',
    shopId: 'all',
    businessCourierId: 'all',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: 50
  });

  // Courier status filter — only for the Order Details table, independent of the report filters
  const [courierStatusIds, setCourierStatusIds] = useState([]);

  const [selectedDatePreset, setSelectedDatePreset] = useState('last30Days');
  const [showFilters, setShowFilters] = useState(true);
  const [activeStatusTab, setActiveStatusTab] = useState(null); // null = all
  const [searchQuery, setSearchQuery] = useState('');
  const [chartViewType, setChartViewType] = useState('count'); // 'count' or 'value'
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  // Export state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportStatus, setExportStatus] = useState('idle');
  const [exportError, setExportError] = useState('');
  const [exportTotalRecords, setExportTotalRecords] = useState(0);
  const [exportFetchedRecords, setExportFetchedRecords] = useState(0);
  const exportAbortRef = useRef(false);

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Set default date range (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setFilters(prev => ({
      ...prev,
      dateFrom: thirtyDaysAgo.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0]
    }));
  }, []);

  // Auto-fetch data when filters are set
  useEffect(() => {
    if (filters.dateFrom && filters.dateTo) {
      handleApplyFilters();
    }
  }, [filters.dateFrom, filters.dateTo]);

  const loadFilterOptions = async () => {
    try {
      setIsLoadingFilters(true);
      const [shopsData, couriersData, courierStatusesData] = await Promise.all([
        getShops(),
        getBusinessCouriers(),
        getCourierStatuses()
      ]);

      setFilterOptions({
        shops: shopsData.shops || [],
        couriers: couriersData.couriers || [],
        courierStatuses: courierStatusesData.courierStatuses || []
      });
    } catch (error) {
      console.error('Failed to load filters:', error);
      showError(t('report.failedToLoadFilters'));
    } finally {
      setIsLoadingFilters(false);
    }
  };

  const getDateRangePreset = (preset) => {
    const today = new Date();
    const from = new Date(today);
    
    switch (preset) {
      case 'today':
        return { from: today, to: today };
      case 'thisWeek':
        from.setDate(today.getDate() - today.getDay());
        return { from, to: today };
      case 'thisMonth':
        from.setDate(1);
        return { from, to: today };
      case 'lastMonth':
        from.setMonth(today.getMonth() - 1);
        from.setDate(1);
        const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return { from, to: lastDayOfLastMonth };
      case 'last30Days':
        from.setDate(today.getDate() - 30);
        return { from, to: today };
      case 'last3Months':
        from.setMonth(today.getMonth() - 3);
        return { from, to: today };
      default:
        return null;
    }
  };

  const handleDatePresetChange = (preset) => {
    setSelectedDatePreset(preset);
    const dateRange = getDateRangePreset(preset);
    if (dateRange) {
      setFilters(prev => ({
        ...prev,
        dateFrom: dateRange.from.toISOString().split('T')[0],
        dateTo: dateRange.to.toISOString().split('T')[0]
      }));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }));
    if (field === 'dateFrom' || field === 'dateTo') {
      setSelectedDatePreset('customRange');
    }
  };

  const validateFilters = () => {
    if (!filters.groupBy) {
      showError(t('report.courierPerformance.groupByRequired'));
      return false;
    }
    if (!filters.dateFrom) {
      showError(t('report.dateFromRequired'));
      return false;
    }
    if (!filters.dateTo) {
      showError(t('report.dateToRequired'));
      return false;
    }
    if (new Date(filters.dateTo) < new Date(filters.dateFrom)) {
      showError(t('report.dateRangeInvalid'));
      return false;
    }
    return true;
  };

  const handleApplyFilters = async () => {
    if (!validateFilters()) return;

    try {
      // Reset details data, visibility, and courier status filter
      setShowOrderDetails(false);
      resetDetails();
      setCourierStatusIds([]);

      // Only fetch summary data
      await fetchSummary(filters);
    } catch (error) {
      console.error('Failed to load data:', error);
      showError(error.response?.data?.message || t('report.failedToLoadData'));
    }
  };

  const handleResetFilters = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setFilters({
      groupBy: 'date',
      shopId: 'all',
      businessCourierId: 'all',
      dateFrom: thirtyDaysAgo.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0],
      page: 1,
      limit: 50
    });
    setSelectedDatePreset('last30Days');
    setActiveStatusTab(null);
    setSearchQuery('');
    setCourierStatusIds([]);
    setShowOrderDetails(false);
    resetAll();
  };

  const handleToggleOrderDetails = async () => {
    if (!showOrderDetails) {
      // Load order details
      try {
        await fetchDetails({ ...filters, courierStatusIds }, filters.page, filters.limit, activeStatusTab);
        setShowOrderDetails(true);
      } catch (error) {
        console.error('Failed to load order details:', error);
        showError(error.response?.data?.message || t('report.failedToLoadData'));
      }
    } else {
      // Hide order details
      setShowOrderDetails(false);
    }
  };

  const handleStatusTabClick = async (status) => {
    setActiveStatusTab(status);
    setFilters(prev => ({ ...prev, page: 1 }));

    try {
      await fetchDetails({ ...filters, courierStatusIds }, 1, filters.limit, status);
    } catch (error) {
      console.error('Failed to filter by status:', error);
      showError(error.response?.data?.message || t('report.failedToLoadData'));
    }
  };

  const handlePageChange = async (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));

    try {
      await fetchDetails({ ...filters, courierStatusIds }, newPage, filters.limit, activeStatusTab);
      // Scroll to table
      document.getElementById('orders-table')?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to load page:', error);
      showError(error.response?.data?.message || t('report.failedToLoadData'));
    }
  };

  const handleExportExcel = async () => {
    try {
      const { default: ExcelJS } = await import('exceljs');
      
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Courier Performance Report');

      // Add title
      worksheet.mergeCells('A1:L1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = 'Courier Performance Report';
      titleCell.font = { size: 16, bold: true };
      titleCell.alignment = { horizontal: 'center' };

      // Add filter info
      worksheet.addRow([]);
      worksheet.addRow(['Date Range:', `${filters.dateFrom} to ${filters.dateTo}`]);
      if (summaryData?.filterInfo?.shopName) {
        worksheet.addRow(['Shop:', summaryData.filterInfo.shopName]);
      }
      if (summaryData?.filterInfo?.courierAccountName) {
        worksheet.addRow(['Courier:', `${summaryData.filterInfo.courierAccountName} - ${summaryData.filterInfo.courierCompanyName}`]);
      }
      worksheet.addRow(['Grouping:', filters.groupBy]);
      worksheet.addRow([]);

      // Add summary
      worksheet.addRow(['Summary']);
      const summaryHeaders = worksheet.addRow(['Metric', 'Count', 'Value', 'Rate']);
      summaryHeaders.font = { bold: true };
      summaryHeaders.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE5E7EB' }
      };
      
      worksheet.addRow([
        'Total Delivered',
        summaryData?.overallSummary?.totalDelivered?.count || 0,
        summaryData?.overallSummary?.totalDelivered?.value || 0,
        `${summaryData?.overallSummary?.deliveryRate || 0}%`
      ]);
      worksheet.addRow([
        'Total Returned',
        summaryData?.overallSummary?.totalReturned?.count || 0,
        summaryData?.overallSummary?.totalReturned?.value || 0,
        `${summaryData?.overallSummary?.returnRate || 0}%`
      ]);
      worksheet.addRow([
        'In Process',
        summaryData?.overallSummary?.totalInProcess?.count || 0,
        summaryData?.overallSummary?.totalInProcess?.value || 0,
        `${summaryData?.overallSummary?.inProcessRate || 0}%`
      ]);
      worksheet.addRow([
        'Grand Total',
        summaryData?.overallSummary?.grandTotal?.count || 0,
        summaryData?.overallSummary?.grandTotal?.value || 0,
        ''
      ]);
      worksheet.addRow([]);

      // Add grouped results
      worksheet.addRow(['Grouped Results']);
      const groupedHeaders = [
        'Period/Location',
        'Delivered Count',
        'Delivered Value',
        'Delivered Rate',
        'Returned Count',
        'Returned Value',
        'Returned Rate',
        'In Process Count',
        'In Process Value',
        'In Process Rate',
        'Total Count',
        'Total Value'
      ];

      const groupedHeaderRow = worksheet.addRow(groupedHeaders);
      groupedHeaderRow.font = { bold: true };
      groupedHeaderRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE5E7EB' }
      };

      // Add grouped data
      if (summaryData?.groupedResults) {
        summaryData.groupedResults.forEach(item => {
          worksheet.addRow([
            item.groupLabel,
            item.delivered.count,
            item.delivered.totalValue,
            `${item.delivered.rate}%`,
            item.returned.count,
            item.returned.totalValue,
            `${item.returned.rate}%`,
            item.inProcess.count,
            item.inProcess.totalValue,
            `${item.inProcess.rate}%`,
            item.totalOrders,
            item.totalValue
          ]);
        });
      }

      // Auto-fit columns
      worksheet.columns.forEach(column => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, cell => {
          const cellLength = cell.value ? cell.value.toString().length : 10;
          if (cellLength > maxLength) {
            maxLength = cellLength;
          }
        });
        column.width = Math.min(maxLength + 2, 50);
      });

      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `courier_performance_${timestamp}.xlsx`;

      // Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      success(t('report.exportSuccess'));
    } catch (error) {
      console.error('Export failed:', error);
      showError(t('report.exportFailed'));
    }
  };

  const EXPORT_BATCH_SIZE = 500;

  const handleExportCSV = async () => {
    setExportModalOpen(true);
    setExportStatus('running');
    setExportError('');
    setExportTotalRecords(0);
    setExportFetchedRecords(0);
    exportAbortRef.current = false;

    try {
      const isNotUpdated = courierStatusIds.includes(NOT_UPDATED_VALUE);
      const realCourierStatusIds = courierStatusIds.filter(id => id !== NOT_UPDATED_VALUE);
      const exportParams = {
        ...filters,
        shopId: filters.shopId === 'all' ? undefined : filters.shopId,
        businessCourierId: filters.businessCourierId === 'all' ? undefined : filters.businessCourierId,
        status: activeStatusTab || undefined,
        ...(isNotUpdated && { courierStatusNotUpdated: true }),
        ...(realCourierStatusIds.length > 0 && { courierStatusIds: realCourierStatusIds.join(',') })
      };

      const firstPage = await getCourierPerformanceDetails({ ...exportParams, page: 1, limit: 1 });
      const total = firstPage.total || 0;

      if (total === 0) {
        setExportStatus('error');
        setExportError('No orders to export with the current filters.');
        return;
      }

      setExportTotalRecords(total);
      const allOrders = [];
      const totalPages = Math.ceil(total / EXPORT_BATCH_SIZE);

      for (let page = 1; page <= totalPages; page++) {
        if (exportAbortRef.current) return;
        const response = await getCourierPerformanceDetails({ ...exportParams, page, limit: EXPORT_BATCH_SIZE });
        allOrders.push(...(response.orders || []));
        setExportFetchedRecords(Math.min(allOrders.length, total));
      }

      const headers = [
        'Tracking Number', 'Order Number', 'Courier Account', 'Dispatched Date',
        'Customer Name', 'Shop', 'City', 'District', 'Province',
        'Amount', 'COD Amount', 'Order Status', 'Courier Status', 'Delivery Status'
      ];

      const escapeCell = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

      const rows = allOrders.map(order => [
        order.airwaybillNumber || '',
        order.orderNumber,
        order.courierAccountName || '',
        order.dispatchedDate ? new Date(order.dispatchedDate).toLocaleDateString() : '',
        order.customerName,
        order.shopName,
        order.cityName,
        order.districtName,
        order.provinceName,
        order.grandTotal || 0,
        order.codAmount || 0,
        order.orderStatus,
        order.courierStatusLabel || '',
        order.deliveryStatus
      ].map(escapeCell).join(','));

      const csvContent = [headers.map(escapeCell).join(','), ...rows].join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `courier_performance_orders_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportStatus('done');
    } catch (err) {
      console.error('Export error:', err);
      setExportStatus('error');
      setExportError('Failed to export orders. Please try again.');
    }
  };

  const handleCloseExportModal = () => {
    exportAbortRef.current = true;
    setExportModalOpen(false);
    setExportStatus('idle');
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'delivered':
        return styles.statusDelivered;
      case 'returned':
        return styles.statusReturned;
      case 'in_process':
        return styles.statusInProcess;
      default:
        return styles.statusDefault;
    }
  };

  const getGroupByLabel = () => {
    switch (filters.groupBy) {
      case 'date': return t('report.period');
      case 'week': return t('report.period');
      case 'month': return t('report.period');
      case 'shipping_zone': return t('report.courierPerformance.shippingZone');
      case 'district': return 'District';
      case 'province': return 'Province';
      default: return t('report.period');
    }
  };

  const courierStatusOptions = [
    { id: NOT_UPDATED_VALUE, label: 'Not Updated' },
    ...filterOptions.courierStatuses.map(cs => ({
      id: cs.id,
      label: `${cs.statusLabel || cs.statusCode} - ${cs.courierCompany?.name || 'N/A'}`
    }))
  ];

  const handleCourierStatusApply = async (ids) => {
    setCourierStatusIds(ids);
    setFilters(prev => ({ ...prev, page: 1 }));
    try {
      await fetchDetails({ ...filters, courierStatusIds: ids }, 1, filters.limit, activeStatusTab);
    } catch (error) {
      console.error('Failed to filter by courier status:', error);
      showError(error.response?.data?.message || t('report.failedToLoadData'));
    }
  };

  const filteredOrders = detailsData?.orders?.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.orderNumber?.toLowerCase().includes(query) ||
      order.airwaybillNumber?.toLowerCase().includes(query) ||
      order.customerName?.toLowerCase().includes(query)
    );
  }) || [];

  return (
    <div className={styles.courierPerformancePage}>
      {/* Page Header */}
      <PageHeader
        title={t('report.courierPerformance.title')}
        description={t('report.courierPerformance.subtitle')}
        icon={TrendingUp}
      />

      {/* Filter Section */}
      <div className={styles.filterSection}>
        <div className={styles.filterHeader}>
          <div className={styles.filterHeaderLeft}>
            <Filter size={20} />
            <h2>{t('report.filters')}</h2>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={styles.filterToggle}
          >
            {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>

        {showFilters && (
          <div className={styles.filterContent}>
            {/* Date Range Presets */}
            <div className={styles.filterGroup}>
              <label>{t('report.dateRange')}</label>
              <div className={styles.datePresets}>
                {['today', 'thisWeek', 'thisMonth', 'lastMonth', 'last30Days', 'last3Months', 'customRange'].map(preset => (
                  <button
                    key={preset}
                    onClick={() => handleDatePresetChange(preset)}
                    className={`${styles.presetButton} ${selectedDatePreset === preset ? styles.active : ''}`}
                  >
                    {t(`report.courierPerformance.${preset}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Date Range */}
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <label>{t('report.dateFrom')} *</label>
                <div className={styles.inputWrapper}>
                  <Calendar className={styles.inputIcon} size={18} />
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className={styles.dateInput}
                    max={filters.dateTo || undefined}
                  />
                </div>
              </div>

              <div className={styles.filterGroup}>
                <label>{t('report.dateTo')} *</label>
                <div className={styles.inputWrapper}>
                  <Calendar className={styles.inputIcon} size={18} />
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className={styles.dateInput}
                    min={filters.dateFrom || undefined}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
            </div>

            {/* Group By */}
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <label>{t('report.courierPerformance.groupBy')} *</label>
                <div className={styles.selectWrapper}>
                  <BarChart3 className={styles.inputIcon} size={18} />
                  <select
                    value={filters.groupBy}
                    onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                    className={styles.select}
                  >
                    <option value="date">{t('report.courierPerformance.byDate')}</option>
                    <option value="week">{t('report.courierPerformance.byWeek')}</option>
                    <option value="month">{t('report.courierPerformance.byMonth')}</option>
                    <option value="shipping_zone">{t('report.courierPerformance.byShippingZone')}</option>
                    <option value="district">{t('report.courierPerformance.byDistrict')}</option>
                    <option value="province">{t('report.courierPerformance.byProvince')}</option>
                  </select>
                  <ChevronDown className={styles.selectArrow} size={18} />
                </div>
              </div>
            </div>

            {/* Shop and Courier Filters */}
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <label>{t('report.shop')}</label>
                <div className={styles.selectWrapper}>
                  <Store className={styles.inputIcon} size={18} />
                  <select
                    value={filters.shopId}
                    onChange={(e) => handleFilterChange('shopId', e.target.value)}
                    className={styles.select}
                  >
                    <option value="all">{t('report.allShops')}</option>
                    {filterOptions.shops.map(shop => (
                      <option key={shop.id} value={shop.id}>
                        {shop.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className={styles.selectArrow} size={18} />
                </div>
              </div>

              <div className={styles.filterGroup}>
                <label>{t('report.courierPerformance.courierAccount')}</label>
                <div className={styles.selectWrapper}>
                  <Truck className={styles.inputIcon} size={18} />
                  <select
                    value={filters.businessCourierId}
                    onChange={(e) => handleFilterChange('businessCourierId', e.target.value)}
                    className={styles.select}
                  >
                    <option value="all">{t('report.courierPerformance.allCouriers')}</option>
                    {filterOptions.couriers.map(courier => (
                      <option key={courier.id} value={courier.id}>
                        {courier.accountName} - {courier.CourierCompany?.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className={styles.selectArrow} size={18} />
                </div>
              </div>
            </div>

            {/* Filter Actions */}
            <div className={styles.filterActions}>
              <button
                onClick={handleResetFilters}
                className={styles.resetButton}
                disabled={isLoadingSummary}
              >
                <RefreshCw size={18} />
                {t('report.resetFilters')}
              </button>
              <button
                onClick={handleApplyFilters}
                className={styles.applyButton}
                disabled={isLoadingSummary}
              >
                {isLoadingSummary ? (
                  <>
                    <Loader2 className={styles.spinner} size={18} />
                    {t('report.applying')}
                  </>
                ) : (
                  <>
                    <BarChart3 size={18} />
                    {t('report.applyFilters')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {summaryData && summaryData.overallSummary && !isLoadingSummary && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon} style={{ backgroundColor: 'var(--color-success-bg)' }}>
              <CheckCircle size={24} style={{ color: 'var(--color-success)' }} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>{t('report.courierPerformance.totalDelivered')}</div>
              <div className={styles.cardValue}>{summaryData.overallSummary.totalDelivered?.count || 0}</div>
              <div className={styles.cardSubtext}>
                {formatCurrency(summaryData.overallSummary.totalDelivered?.value || 0, user?.business?.currency || null)}
              </div>
              <div className={styles.cardBadge} style={{ backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)' }}>
                {summaryData.overallSummary.deliveryRate || 0}% {t('report.courierPerformance.deliveryRate')}
              </div>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <div className={styles.cardIcon} style={{ backgroundColor: 'var(--color-error-bg)' }}>
              <XCircle size={24} style={{ color: 'var(--color-error)' }} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>{t('report.courierPerformance.totalReturned')}</div>
              <div className={styles.cardValue}>{summaryData.overallSummary.totalReturned?.count || 0}</div>
              <div className={styles.cardSubtext}>
                {formatCurrency(summaryData.overallSummary.totalReturned?.value || 0, user?.business?.currency || null)}
              </div>
              <div className={styles.cardBadge} style={{ backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)' }}>
                {summaryData.overallSummary.returnRate || 0}% {t('report.courierPerformance.returnRate')}
              </div>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <div className={styles.cardIcon} style={{ backgroundColor: 'var(--color-info-bg)' }}>
              <Clock size={24} style={{ color: 'var(--color-info)' }} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>{t('report.courierPerformance.inProcess')}</div>
              <div className={styles.cardValue}>{summaryData.overallSummary.totalInProcess?.count || 0}</div>
              <div className={styles.cardSubtext}>
                {formatCurrency(summaryData.overallSummary.totalInProcess?.value || 0, user?.business?.currency || null)}
              </div>
              <div className={styles.cardBadge} style={{ backgroundColor: 'var(--color-info-bg)', color: 'var(--color-info)' }}>
                {summaryData.overallSummary.inProcessRate || 0}% {t('report.courierPerformance.inProcessRate')}
              </div>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <div className={styles.cardIcon} style={{ backgroundColor: 'var(--color-gray-200)' }}>
              <Package size={24} style={{ color: 'var(--color-gray-500)' }} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>{t('report.courierPerformance.grandTotal')}</div>
              <div className={styles.cardValue}>{summaryData.overallSummary.grandTotal?.count || 0}</div>
              <div className={styles.cardSubtext}>
                {formatCurrency(summaryData.overallSummary.grandTotal?.value || 0, user?.business?.currency || null)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chart Visualization */}
      {summaryData && summaryData.overallSummary && !isLoadingSummary && (
        <div className={styles.chartSection}>
          <div className={styles.chartHeader}>
            <h2>{t('report.chartVisualization')}</h2>
            <div className={styles.chartHeaderActions}>
              <div className={styles.chartToggle}>
                <button
                  onClick={() => setChartViewType('count')}
                  className={`${styles.toggleButton} ${chartViewType === 'count' ? styles.active : ''}`}
                >
                  {t('report.courierPerformance.showByCount')}
                </button>
                <button
                  onClick={() => setChartViewType('value')}
                  className={`${styles.toggleButton} ${chartViewType === 'value' ? styles.active : ''}`}
                >
                  {t('report.courierPerformance.showByValue')}
                </button>
              </div>
              <button onClick={handleExportExcel} className={styles.exportButton}>
                <Download size={18} />
                {t('report.exportToExcel')}
              </button>
            </div>
          </div>

          <div className={styles.chartContainer}>
            {summaryData.groupedResults && summaryData.groupedResults.length > 0 ? (
              <div style={{ height: '400px', position: 'relative' }}>
                <Bar
                  data={{
                    labels: summaryData.groupedResults.map(item => item.groupLabel),
                    datasets: [
                      {
                        label: t('report.courierPerformance.delivered'),
                        data: summaryData.groupedResults.map(item => 
                          chartViewType === 'count' ? item.delivered.count : item.delivered.totalValue
                        ),
                        extraInfo: summaryData.groupedResults.map(item => item.delivered.rate),
                        backgroundColor: 'rgba(16, 185, 129, 0.8)',
                        borderColor: 'rgba(16, 185, 129, 1)',
                        borderWidth: 1
                      },
                      {
                        label: t('report.courierPerformance.returned'),
                        data: summaryData.groupedResults.map(item => 
                          chartViewType === 'count' ? item.returned.count : item.returned.totalValue
                        ),
                        extraInfo: summaryData.groupedResults.map(item => item.returned.rate),
                        backgroundColor: 'rgba(239, 68, 68, 0.8)',
                        borderColor: 'rgba(239, 68, 68, 1)',
                        borderWidth: 1
                      },
                      {
                        label: t('report.courierPerformance.inProcess'),
                        data: summaryData.groupedResults.map(item => 
                          chartViewType === 'count' ? item.inProcess.count : item.inProcess.totalValue
                        ),
                        extraInfo: summaryData.groupedResults.map(item => item.inProcess.rate),
                        backgroundColor: 'rgba(59, 130, 246, 0.8)',
                        borderColor: 'rgba(59, 130, 246, 1)',
                        borderWidth: 1
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            let label = context.dataset.label || '';
                            if (label) {
                              label += ': ';
                            }
                            if (context.parsed.y !== null) {
                              if (chartViewType === 'value') {
                                label += formatCurrency(context.parsed.y);
                              } else {
                                label += context.parsed.y.toLocaleString();
                              }
                              label += " ("+context.dataset.extraInfo[context.dataIndex]+"%)";
                            }
                            return label;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        stacked: true,
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45
                        }
                      },
                      y: {
                        stacked: true,
                        beginAtZero: true,
                        ticks: {
                          callback: function(value) {
                            if (chartViewType === 'value') {
                              return 'Rs. ' + value.toLocaleString();
                            }
                            return value.toLocaleString();
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <div className={styles.emptyChart}>
                <BarChart3 size={48} />
                <p>{t('report.noDataAvailable')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Grouped Results Table */}
      {summaryData && !isLoadingSummary && summaryData.groupedResults && summaryData.groupedResults.length > 0 && (
        <div className={styles.groupedResultsSection}>
          <div className={styles.groupedResultsHeader}>
            <h2>{t('report.dataTable')}</h2>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>{getGroupByLabel()}</th>
                  <th colSpan="3" className={styles.groupHeader}>{t('report.courierPerformance.delivered')}</th>
                  <th colSpan="3" className={styles.groupHeader}>{t('report.courierPerformance.returned')}</th>
                  <th colSpan="3" className={styles.groupHeader}>{t('report.courierPerformance.inProcess')}</th>
                  <th colSpan="2" className={styles.groupHeader}>Total</th>
                </tr>
                <tr>
                  <th></th>
                  <th>Count</th>
                  <th>Value</th>
                  <th>Rate</th>
                  <th>Count</th>
                  <th>Value</th>
                  <th>Rate</th>
                  <th>Count</th>
                  <th>Value</th>
                  <th>Rate</th>
                  <th>Count</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {summaryData.groupedResults.map((item, index) => (
                  <tr key={index} className={styles.dataRow}>
                    <td className={styles.groupLabelCell}>{item.groupLabel}</td>
                    <td>{item.delivered.count}</td>
                    <td className={styles.valueCell}>{formatCurrency(item.delivered.totalValue)}</td>
                    <td className={styles.rateCell}>{item.delivered.rate}%</td>
                    <td>{item.returned.count}</td>
                    <td className={styles.valueCell}>{formatCurrency(item.returned.totalValue)}</td>
                    <td className={styles.rateCell}>{item.returned.rate}%</td>
                    <td>{item.inProcess.count}</td>
                    <td className={styles.valueCell}>{formatCurrency(item.inProcess.totalValue)}</td>
                    <td className={styles.rateCell}>{item.inProcess.rate}%</td>
                    <td className={styles.totalCell}>{item.totalOrders}</td>
                    <td className={`${styles.valueCell} ${styles.totalCell}`}>{formatCurrency(item.totalValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View Order Details Button */}
      {summaryData && summaryData.overallSummary && !isLoadingSummary && (
        <div className={styles.orderDetailsToggle}>
          <button
            onClick={handleToggleOrderDetails}
            className={styles.toggleOrderDetailsButton}
            disabled={isLoadingDetails}
          >
            {isLoadingDetails ? (
              <>
                <Loader2 className={styles.spinner} size={20} />
                Loading...
              </>
            ) : showOrderDetails ? (
              <>
                <EyeOff size={20} />
                Hide Order Details
              </>
            ) : (
              <>
                <Eye size={20} />
                View Order Details
              </>
            )}
          </button>
        </div>
      )}

      {/* Orders Table - Only show when showOrderDetails is true */}
      {showOrderDetails && detailsData && !isLoadingDetails && (
        <div className={styles.tableSection} id="orders-table">
          <div className={styles.tableHeader}>
            <h2>{t('report.courierPerformance.orderDetails')}</h2>
            <div className={styles.tableHeaderActions}>
              <div className={styles.searchBox}>
                <Search className={styles.searchIcon} size={18} />
                <input
                  type="text"
                  placeholder={t('report.courierPerformance.searchOrders')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              <button onClick={handleExportCSV} className={styles.exportButton}>
                <Download size={18} />
                Export CSV
              </button>
            </div>
          </div>

          {/* Status Tabs */}
          <div className={styles.statusTabs}>
            <button
              onClick={() => handleStatusTabClick(null)}
              className={`${styles.statusTab} ${activeStatusTab === null ? styles.active : ''}`}
            >
              {t('report.courierPerformance.all')}
            </button>
            <button
              onClick={() => handleStatusTabClick('delivered')}
              className={`${styles.statusTab} ${activeStatusTab === 'delivered' ? styles.active : ''}`}
            >
              {t('report.courierPerformance.delivered')}
            </button>
            <button
              onClick={() => handleStatusTabClick('returned')}
              className={`${styles.statusTab} ${activeStatusTab === 'returned' ? styles.active : ''}`}
            >
              {t('report.courierPerformance.returned')}
            </button>
            <button
              onClick={() => handleStatusTabClick('in_process')}
              className={`${styles.statusTab} ${activeStatusTab === 'in_process' ? styles.active : ''}`}
            >
              {t('report.courierPerformance.inProcess')}
            </button>
          </div>

          {/* Courier Status Filter */}
          <div className={styles.courierStatusFilterRow}>
            <label className={styles.courierStatusFilterLabel}>Courier Status</label>
            <div className={styles.courierStatusFilterSelect}>
              <CourierStatusMultiSelect
                options={courierStatusOptions}
                committedIds={courierStatusIds}
                onApply={handleCourierStatusApply}
              />
            </div>
          </div>

          <div className={styles.tableContainer}>
            {filteredOrders.length > 0 ? (
              <>
                <div className={styles.tableInfo}>
                  {t('report.showing')} {((filters.page - 1) * filters.limit) + 1} {t('report.to')}{' '}
                  {Math.min(filters.page * filters.limit, detailsData.total)} {t('report.of')}{' '}
                  {detailsData.total} {t('report.courierPerformance.orders')}
                </div>

                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>{t('report.courierPerformance.trackingNumber')}</th>
                      <th>{t('report.orderNumber')}</th>
                      <th>{t('report.courierPerformance.courierAccount')}</th>
                      <th>{t('report.courierPerformance.dispatchedDate')}</th>
                      <th>{t('report.courierPerformance.customerName')}</th>
                      <th>{t('report.shopName')}</th>
                      <th>{t('report.courierPerformance.location')}</th>
                      <th>{t('report.courierPerformance.amount')}</th>
                      <th>{t('report.courierPerformance.codAmount')}</th>
                      <th>{t('report.status')}</th>
                      <th>Courier Status</th>
                      <th>{t('report.courierPerformance.deliveryStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order, index) => (
                      <tr key={index} className={styles.dataRow}>
                        <td className={styles.trackingCell}>{order.airwaybillNumber || 'N/A'}</td>
                        <td className={styles.orderNumberCell}>{order.orderNumber}</td>
                        <td>{order.courierAccountName || 'N/A'}</td>
                        <td>{new Date(order.dispatchedDate).toLocaleDateString()}</td>
                        <td>{order.customerName}</td>
                        <td>{order.shopName}</td>
                        <td>
                          <div className={styles.locationCell}>
                            <div>{order.cityName}</div>
                            <div className={styles.locationSub}>
                              {order.districtName} → {order.provinceName}
                            </div>
                          </div>
                        </td>
                        <td className={styles.valueCell}>{formatCurrency(order.grandTotal)}</td>
                        <td className={styles.valueCell}>{formatCurrency(order.codAmount)}</td>
                        <td>{order.orderStatus}</td>
                        <td>
                          {order.courierStatusLabel
                            ? <span className={styles.courierStatusBadge}>{order.courierStatusLabel}</span>
                            : <span className={styles.naText}>—</span>
                          }
                        </td>
                        <td>
                          <span className={`${styles.statusBadge} ${getStatusBadgeClass(order.deliveryStatus)}`}>
                            {t(`report.courierPerformance.${order.deliveryStatus}`)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {detailsData.total > filters.limit && (
                  <div className={styles.pagination}>
                    <div className={styles.paginationButtons}>
                      <button
                        onClick={() => handlePageChange(filters.page - 1)}
                        disabled={filters.page === 1}
                        className={styles.pageButton}
                      >
                        {t('report.previous')}
                      </button>
                      <span className={styles.pageInfo}>
                        {t('report.page')} {filters.page} {t('report.of')}{' '}
                        {Math.ceil(detailsData.total / filters.limit)}
                      </span>
                      <button
                        onClick={() => handlePageChange(filters.page + 1)}
                        disabled={filters.page >= Math.ceil(detailsData.total / filters.limit)}
                        className={styles.pageButton}
                      >
                        {t('report.next')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyTable}>
                <Package size={48} />
                <h3>{t('report.noOrdersFound')}</h3>
                <p>{t('report.noOrdersMessage')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoadingSummary && !summaryData && (
        <div className={styles.loadingState}>
          <Loader2 className={styles.spinnerLarge} size={48} />
          <h3>{t('report.loadingData')}</h3>
        </div>
      )}

      {/* Empty State */}
      {!summaryData && !isLoadingSummary && (
        <div className={styles.emptyState}>
          <TrendingUp size={64} />
          <h3>{t('report.courierPerformance.noDataYet')}</h3>
          <p>{t('report.courierPerformance.selectFilters')}</p>
        </div>
      )}

      <ExportProgressModal
        isOpen={exportModalOpen}
        onClose={handleCloseExportModal}
        status={exportStatus}
        error={exportError}
        totalRecords={exportTotalRecords}
        fetchedRecords={exportFetchedRecords}
      />
    </div>
  );
};

export default CourierPerformancePage;